export function titleCase(val: string): string {
  const words: string[] = val.toLowerCase().split(" ");
  return words
    .map((word: string): string => {
      // the string, word becomes Word so each "word" is capitalized.
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/**
 * Break the string up into an array of constituent parts, typically separated by a comma ( and maybe some optional whitespace )
 */
const countOccurrence = (value: string, separatorToken = /,\s*/) => {
  return value.split(separatorToken).length;
};

/**
 * When the field contains more than the permitted number of entries, prevent additional typing,
 * unless it is to remove text or navigate with the four arrow keys.
 */
export const handleMaxOccurrenceLimit = (
  e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  maxOccurrence: number
) => {
  const count = countOccurrence(e.currentTarget.value);
  if (
    count > maxOccurrence &&
    e.key !== "Home" &&
    e.key !== "End" &&
    e.key !== "Delete" &&
    e.key !== "Backspace" &&
    e.key !== "ArrowUp" &&
    e.key !== "ArrowDown" &&
    e.key !== "ArrowLeft" &&
    e.key !== "ArrowRight"
  ) {
    e.preventDefault();
  }
};

/**
 * Converts a string from camelCase to snake_case
 * @param inputString
 *
 * example: "helloWorld" => "hello_world"
 * example: "ipv4Addr" => "ipv4_addr"
 */
export function camelToSnakeCase(inputString: string): string {
  return inputString.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
}

/**
 * Converts a string from snake_case to Display Label
 * @param inputString
 *
 * example: "hello_world" => "Hello world"
 */
export function snakeCaseToLabel(inputString: string): string {
  return titleCase(inputString.replaceAll("_", " "));
}

export function autocorrectString(str: string) {
  // Remove any characters that are not alphanumeric or hyphens
  return str.replace(/[^a-zA-Z0-9-]*/g, "");
}

export const kebabCase = (id: string) => {
  return id.toLowerCase().trim().replaceAll(" ", "-");
};

/**
 * Returns an appropriate string based on the count value, supporting distinct messages for zero, one, and multiple items.
 * Useful for generating grammatically correct labels and messages that change based on quantity.
 *
 * @param count - The numeric count that determines which string to return. Defaults to 0 if not provided.
 * @param single - The string to return when count is exactly 1 (e.g., "1 item", "One device").
 * @param plural - The string to return when count is 2 or greater (e.g., "N items", "Multiple devices").
 * @param none - Optional. The string to return when count is exactly 0 (e.g., "No items", "Nothing selected").
 *              Defaults to the plural string if not provided.
 *
 * @returns The appropriate string based on the count value.
 *
 * @example
 * // Basic usage - most common pattern where zero case uses plural form
 * pluralize(0, "1 item", "5 items")  // Returns "5 items"
 * pluralize(1, "1 item", "5 items")  // Returns "1 item"
 * pluralize(5, "1 item", "5 items")  // Returns "5 items"
 *
 * @example
 * // With explicit zero message
 * pluralize(0, "1 item", "5 items", "No items")  // Returns "No items"
 * pluralize(1, "1 item", "5 items", "No items")  // Returns "1 item"
 * pluralize(5, "1 item", "5 items", "No items")  // Returns "5 items"
 *
 * @example
 * // Dynamic message generation
 * const count = devices.length;
 * const message = pluralize(
 *   count,
 *   "1 device selected",
 *   `${count} devices selected`,
 *   "No devices available"
 * );
 *
 * @example
 * // Conditional UI rendering
 * <Text>
 *   {pluralize(errorCount, "1 error found", `${errorCount} errors found`, "No errors")}
 * </Text>
 *
 * @example
 * // Simple case without zero handling (common for counts that are always > 0)
 * const label = pluralize(selectedItems.length, "item", "items");
 *
 * @remarks
 * - The `none` parameter is optional and defaults to the `plural` value. This is useful for simple cases
 *   like "0 items" where the plural form is grammatically correct for zero.
 * - Negative numbers are treated as plural (default case).
 * - For languages with complex plural rules (e.g., Slavic languages with different forms for 2-4 vs 5+),
 *   consider using a dedicated i18n library like `i18next` or `Intl.PluralRules`.
 * - The function performs strict equality checks (===), so fractional values like 1.5 will use the plural form.
 */
export const pluralize = (
  count: number = 0,
  single: string,
  plural: string,
  none: string = plural
) => {
  switch (count) {
    case 0:
      return none;
    case 1:
      return single;
    default:
      return plural;
  }
};

/**
 * Creates a composite key for map lookups by joining values with a separator.
 * Default separator is tilde (~) to match existing codebase conventions.
 *
 * Returns undefined if any value is empty/undefined/null to prevent:
 * - Key collisions from partial keys
 * - Invalid map entries with undefined as key
 *
 * @param values - Values to join into a composite key
 * @param separator - Separator string (defaults to "~")
 * @returns Composite key string, or undefined if any value is empty
 */
export const createCompositeKey = (
  values: (string | undefined | null)[],
  separator: string = "~"
): string | undefined => {
  if (values.some((v) => !v)) {
    return undefined;
  }
  return values.join(separator);
};

/**
 * Extracts all digits from a string, removing all non-digit characters.
 *
 * @param str - The input string to extract digits from
 * @returns A string containing only the digits from the input
 *
 * @example
 * extractDigits("400G") // Returns "400"
 * extractDigits("abc123def456") // Returns "123456"
 */
export const extractDigits = (str: string): string => {
  return str.replace(/\D/g, "");
};
